<script setup lang="ts">
import { ChevronDown, ChevronRight, Folder, FolderOpen, GripVertical } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import CreateGroup from '@/components/CreateGroup.vue'
import { Ref, inject, ref, h, type VNode, watch, onMounted, onBeforeUnmount } from 'vue'
import { Toaster } from '@/components/ui/sonner'
import { toast } from 'vue-sonner'
import type { Group } from '@/types/Groups'
import DOMPurify from 'dompurify'

import { useRouter } from 'vue-router'
import { useGroups } from '@/services/Groups'
import permissions from '@/services/permissions'
const router = useRouter()
const searchQuery = ref('')
const filteredGroupTree = ref<Group[]>([])
const { checkPermission } = permissions()
const { isReady, queueMovement, groupTree, getGroupTree } = useGroups()
const loading = ref(true)
const vhostId = inject<Ref<string> | null>('vhostId', null)
const expandedGroups = ref<Set<number>>(new Set())
const pendingChanges = ref<{gid: number, newParent: number, leftSibling: number}[]>([])
const isProcessingChanges = ref(false)
const lastUpdateCheck = ref<Date | null>(null)
const hoverTimer = ref<NodeJS.Timeout | null>(null)
const lastHoveredGroup = ref<number | null>(null)

const pollInterval = ref<NodeJS.Timeout | null>(null)
const filterGroups = (groups: Group[], query: string, parentNames: string[] = []): Group[] => {
	if (!query) return groups

	return groups.reduce((result: Group[], group: Group) => {
		const currentPath = [...parentNames, group.name]
		const matches = group.name.toLowerCase().includes(query.toLowerCase()) || 
			(group.description && group.description.toLowerCase().includes(query.toLowerCase()))

		// Check children recursively
		const childMatches = group.children ? filterGroups(group.children, query, currentPath) : []

		if (matches || childMatches.length > 0) {
			// Clone the group to avoid modifying the original
			const matchedGroup = { ...group }

			// If we're showing because of child matches, add the ancestry info
			if (!matches && childMatches.length > 0) {
				matchedGroup._showAncestry = true
				matchedGroup._ancestryPath = parentNames.join(' > ')
			}

			// Set the filtered children
			matchedGroup.children = childMatches

			result.push(matchedGroup)
		}

		return result
	}, [])
}
watch([searchQuery, groupTree], () => {
	if (!searchQuery.value) {
		filteredGroupTree.value = groupTree.value
		return
	}

	filteredGroupTree.value = filterGroups(groupTree.value, searchQuery.value)
}, { immediate: true })

// Start/stop polling when component mounts/unmounts
onMounted(() => {
	if(vhostId) {
		fetchGroupTree(vhostId.value);
	}
	startPolling()
})
const getDropPosition = (e: DragEvent, element: HTMLElement): 'before' | 'into' | 'after' => {
	const rect = element.getBoundingClientRect()
	const y = e.clientY - rect.top
	const height = rect.height
	const threshold = height / 3

	if (y < threshold) return 'before'
	if (y > height - threshold) return 'after'
	return 'into'
}
const fetchGroupTree = async (vhost:string) => {
	if(vhost) {
		await getGroupTree(vhost);
		loading.value=false
	}
}

onBeforeUnmount(() => {
	stopPolling()
	if (hoverTimer.value) {
		clearTimeout(hoverTimer.value)
	}
})

const startPolling = () => {
	// Check immediately first
	checkForCompletedChanges()

	// Then every 30 seconds
	pollInterval.value = setInterval(checkForCompletedChanges, 3000)
}

const stopPolling = () => {
	if (pollInterval.value) {
		clearInterval(pollInterval.value)
		pollInterval.value = null
	}
}





const emit = defineEmits(['reorder', 'refresh'])

// Drag and drop state
const draggedItem = ref<Group | null>(null)
type DropPosition = 'before' | 'into' | 'after'
type DragOverItem = {
	    group: Group
	    position: DropPosition
}

const dragOverItem = ref<DragOverItem | null>(null)
const isDragging = ref(false)

const toggleExpand = (groupId: number) => {
	if (expandedGroups.value.has(groupId)) {
		expandedGroups.value.delete(groupId)
	} else {
		expandedGroups.value.add(groupId)
	}
}

const isExpanded = (groupId: number): boolean => {
	return expandedGroups.value.has(groupId)
}

const getPadding = (depth: number): string => {
	return `${12 + (depth * 24)}px`
}

// Find the siblings of a group at its current position in the tree
const getSiblings = (groupId: number): Group[] => {
	if (!groupTree.value) return []

	// Check root level
	const rootIndex = groupTree.value.findIndex(g => g.id === groupId)
	if (rootIndex >= 0) return groupTree.value

	// Search recursively for the parent containing this group
	const findParentChildren = (nodes: Group[]): Group[] | null => {
		for (const node of nodes) {
			if (node.children) {
				if (node.children.some(c => c.id === groupId)) {
					return node.children
				}
				const found = findParentChildren(node.children)
				if (found) return found
			}
		}
		return null
	}

	return findParentChildren(groupTree.value) || []
}

// Find the immediate left sibling of a group within its sibling list
const findLeftSiblingOf = (targetId: number, siblings: Group[]): number => {
	const index = siblings.findIndex(s => s.id === targetId)
	if (index > 0) return siblings[index - 1].id
	return -1 // first child position
}
// Add new drop position type

// Track drop position state
const dropPosition = ref<{id: number, position: DropPosition} | null>(null)

// Drag and drop handlers
const onDragStart = (group: Group) => {
	draggedItem.value = group
	isDragging.value = true
}
// Update onDragOver to detect position
const onDragOver = (e: DragEvent, group: Group) => {
	e.preventDefault()
	if (!draggedItem.value || draggedItem.value.id === group.id) return

	// Determine drop position
	const dropPosition = getDropPosition(e, e.currentTarget as HTMLElement)

	// Update visual feedback
	const rowElement = e.currentTarget as HTMLElement
	rowElement.classList.remove('drag-over-before', 'drag-over-after', 'drag-over-into')

	if (dropPosition === 'before') {
		rowElement.classList.add('drag-over-before')
	} else if (dropPosition === 'after') {
		rowElement.classList.add('drag-over-after')
	} else {
		rowElement.classList.add('drag-over-into')

		// Auto-expand logic for folders
		if (!isExpanded(group.id)) {
			// If we're hovering over a new closed folder
			if (lastHoveredGroup.value !== group.id) {
				// Clear any existing timer
				if (hoverTimer.value) {
					clearTimeout(hoverTimer.value)
					hoverTimer.value = null
				}

				// Store the currently hovered group
				lastHoveredGroup.value = group.id

				// Set new timer to expand after 600ms
				hoverTimer.value = setTimeout(() => {
					expandedGroups.value.add(group.id!)
					hoverTimer.value = null
					lastHoveredGroup.value = null
				}, 600)
			}
		}
	}
	dragOverItem.value = {
		    group: group,
		    position: getDropPosition(e, e.currentTarget as HTMLElement)
	}
}
const findNode = (nodes: Group[], id: number): Group | null => {
	for (const node of nodes) {
		if (node.id === id) return node
		if (node.children) {
			const found = findNode(node.children, id)
			if (found) return found
		}
	}
	return null
}

const removeNode = (nodes: Group[], id: number): boolean => {
	const index = nodes.findIndex(n => n.id === id)
	if (index >= 0) {
		nodes.splice(index, 1)
		return true
	}
	for (const node of nodes) {
		if (node.children && removeNode(node.children, id)) {
			return true
		}
	}
	return false
}


const onDragLeave = () => {
	// Clear any pending expansion timer
	if (hoverTimer.value) {
		clearTimeout(hoverTimer.value)
		hoverTimer.value = null
	}
	lastHoveredGroup.value = null
	dragOverItem.value = null
}
const onDragEnd = () => {
	isDragging.value = false
	draggedItem.value = null
	dragOverItem.value = null

	// Clear any pending expansion timer
	if (hoverTimer.value) {
		clearTimeout(hoverTimer.value)
		hoverTimer.value = null
	}
	lastHoveredGroup.value = null

	// Clean up any lingering CSS classes from drag-over
	document.querySelectorAll('.drag-over-before, .drag-over-after, .drag-over-into').forEach(el => {
		el.classList.remove('drag-over-before', 'drag-over-after', 'drag-over-into')
	})
}

const onDrop = async (e: DragEvent, targetGroup: Group) => {
	e.preventDefault()
	isDragging.value = false

	// Clear any pending expansion timer
	if (hoverTimer.value) {
		clearTimeout(hoverTimer.value)
		hoverTimer.value = null
	}
	dragOverItem.value = null

	if (!draggedItem.value || draggedItem.value.id === targetGroup.id) return

	// Prevent dropping a parent into its own descendant
	if (isDescendant(draggedItem.value, targetGroup)) {
		toast.error('Cannot move a group into its own descendant')
		draggedItem.value = null
		return
	}

	// Determine drop position
	const dropPos = getDropPosition(e, e.currentTarget as HTMLElement)
	let newParentId: number
	let leftSibling: number

	if (dropPos === 'into') {
		// Dropping into the group (as child) — place as first child
		newParentId = targetGroup.id!
		leftSibling = -1
	} else {
		// Dropping before/after (as sibling of the target)
		newParentId = targetGroup.parentGroup ?? 0
		const siblings = getSiblings(targetGroup.id)

		if (dropPos === 'before') {
			// Place before the target — find its left sibling
			leftSibling = findLeftSiblingOf(targetGroup.id, siblings)
		} else {
			// Place after the target — target becomes the left sibling
			leftSibling = targetGroup.id!
		}
	}

	// Add to pending changes
	pendingChanges.value.push({
		gid: draggedItem.value.id,
		newParent: newParentId,
		leftSibling: leftSibling
	})

	// Immediately update UI (optimistic update)
	updateLocalTree(draggedItem.value.id, newParentId, leftSibling)

	// Queue the change
	try {
		if (draggedItem.value.vhost === undefined) {
			throw new Error('Missing vhost on dragged item — cannot continue')
		}
		await queuePositionChange(+draggedItem.value.id, +newParentId, +leftSibling)
	} catch (error) {
		console.error('Failed to queue change:', error)
		// Rollback the optimistic update if needed
		toast.error('Failed to move group')
	} finally {
		draggedItem.value = null
	}
}
const updateLocalTree = (movedId: number, newParentId: number, leftSiblingId: number) => {
  const nodeToMove = findNode(groupTree.value, movedId)
  if (!nodeToMove) return

  // Remove from old position
  removeNode(groupTree.value, movedId)

  // Find destination parent
  let parentNode: Group[] | Group | null = null
  if (newParentId === 0) {
    parentNode = groupTree.value
  } else {
    const parentGroup = findNode(groupTree.value, newParentId)
    if (parentGroup) {
      parentGroup.children = parentGroup.children || []
      parentNode = parentGroup.children
    }
  }

  if (!parentNode) {
    console.warn('Parent not found, adding to root')
    parentNode = groupTree.value
  }

  // Find insert index
  if (Array.isArray(parentNode)) {
    const index = parentNode.findIndex((c) => c.id === leftSiblingId)
    if (index >= 0) {
      parentNode.splice(index + 1, 0, nodeToMove)
    } else {
      parentNode.unshift(nodeToMove)
    }
  }

  // Force reactivity
  groupTree.value = [...groupTree.value]
}

// Updated updateLocalTree to handle moves within same parent
// Helper to check if a group is a descendant of another
const isDescendant = (parent: Group, child: Group): boolean => {
	if (!parent.children) return false
	if (parent.children.some(g => g.id === child.id)) return true
	return parent.children.some(g => isDescendant(g, child))
}

// Updated updateLocalTree to properly handle root level moves
// Update findParent to handle root level
const findParent = (nodes: Group[], childId: number): Group | null => {
	// First check if it's a direct child of root
	if (nodes.some(node => node.id === childId)) {
		return null // It's a root-level group
	}

	// Then check nested children
	for (const node of nodes) {
		if (node.children) {
			if (node.children.some(child => child.id === childId)) {
				return node
			}
			const found = findParent(node.children, childId)
			if (found) return found
		}
	}
	return null
}

// Update findPreviousSibling to handle root level
const findPreviousSibling = (siblings: Group[], id: number): number => {
	const index = siblings.findIndex(s => s.id === id)
	return index > 0 ? siblings[index - 1].id! : 0
}

// Optimistically update the local tree

const queuePositionChange = async (gid: number, parentGroup: number, leftSibling: number) => {
	if (vhostId) {
		const response = await queueMovement(+vhostId.value, gid,parentGroup,leftSibling);
		if (!response) {
			await getGroupTree(vhostId.value);

			// Show notification
			pendingChanges.value=[]; 
			return true;
		}

		return response;
	} 
	return false;
}

const checkForCompletedChanges = async () => {
	if (!pendingChanges.value.length) return

	try {
		if (vhostId) {
			const response = await isReady(vhostId.value)
			if (response) {
				await getGroupTree(vhostId.value);

				// Show notification
				showCompletionNotification(1)
				pendingChanges.value=[];
			}
		}
	} catch (error) {
		console.error('Error checking for completed changes:', error)
	}
}

const showCompletionNotification = (completedCount: number) => {
	toast('Changes Applied', {
		description: `${completedCount} pending ${completedCount === 1 ? 'change has' : 'changes have'} been processed.`,
		duration: 5000
	});


}
// Navigation to group detail page replaces the old edit modal
const renderGroupRow = (group: Group, depth = 0): VNode[] => {
	const isPending = pendingChanges.value.some(c => c.gid === group.id)
	const children: VNode[] = group.children?.length && isExpanded(group.id)
		? group.children.flatMap(child => renderGroupRow(child, depth + 1))
		: []

	return [
		h(TableRow, { 
			class: [
				    'hover:bg-muted/20 relative p-2',
				    draggedItem.value?.id === group.id ? 'opacity-80' : '',
				    dragOverItem.value?.group.id === group.id && dragOverItem.value?.position === 'before' ? 'border-t-8 pt-5 border-blue-200 border-b-0' : '',
				    dragOverItem.value?.group.id === group.id && dragOverItem.value?.position === 'after' ? 'border-b-8 border-blue-200 border-t-0' : '',
				    dragOverItem.value?.group.id === group.id && dragOverItem.value?.position === 'into' ? 'bg-blue-100' : '',
				    isPending ? 'bg-yellow-50' : ''
				],
			draggable: true,
			onDragstart: () => onDragStart(group),
			onDragover: (e: DragEvent) => onDragOver(e, group),
			onDragleave: onDragLeave,
			onDragend: onDragEnd,
			onDrop: (e: DragEvent) => onDrop(e, group)
		}, [
			h(TableCell, { class: 'font-medium p-2', style: { paddingLeft: getPadding(depth) } }, [
				h('span', { class: 'inline-flex items-center' }, [
					h(GripVertical, { class: 'h-4 w-4 mr-1 text-muted-foreground/50 cursor-grab shrink-0' }),
					group.children?.length 
					? h(Button, {
						variant: 'ghost',
						size: 'sm',
						class: 'h-8 w-8 p-0 mr-2',
						onClick: () => toggleExpand(group.id)
					}, [
						isExpanded(group.id)
						? h(ChevronDown, { class: 'h-4 w-4' })
						: h(ChevronRight, { class: 'h-4 w-4' })
					])
					: h('span', { class: 'w-8 mr-2 inline-block' }),

					group.children?.length
					? isExpanded(group.id)
					? h(FolderOpen, { class: 'h-4 w-4 mr-2 inline' })
					: h(Folder, { class: 'h-4 w-4 mr-2 inline' })
					: h('span', { class: 'w-4 mr-2 inline-block' }),

					h('span', { class: isPending ? 'text-yellow-600' : '' }, [
						// Add ancestry path if this group is shown because of child match
						group._showAncestry
						? h('span', { class: 'text-muted-foreground text-xs mr-2' }, `${group._ancestryPath} >`)
						: null,

						h('span', {
							class: 'cursor-pointer hover:underline hover:text-blue-600',
							onClick: (e: MouseEvent) => {
								e.stopPropagation()
								router.push(`/groups/${group.id}`)
							}
						}, group.name),
						isPending ? h('span', { class: 'ml-2 text-xs text-yellow-600' }, '(Pending move)') : null
					])
				])
			]),
			h(TableCell, { class: 'p-2 text-muted-foreground' }, [
				group.description ? h('div', { 
					class: 'group-description',
					innerHTML: DOMPurify.sanitize(group.description)
				}) : null
			]),
			h(TableCell, group.mediaCount),
			h(TableCell, group.memberCount),
			h(TableCell, [
				h(Badge, {
					variant: group.moderationstatus === 1 ? 'default' 
					: group.moderationstatus === 0 ? 'secondary'
					: 'destructive'
				}, 
					group.moderationstatus === 1 ? 'Approved' 
					: group.moderationstatus === 2 ? 'Denied'
					: group.moderationstatus === 8 ? 'Deleted'
					: 'Unmoderated')
			]),
			h(TableCell, { class: 'p-2 text-right' }, [
				h(Button, {
					variant: 'ghost',
					size: 'sm',
					class: 'h-8',
					onClick: () => router.push(`/groups/${group.id}`)
				}, 'Manage')
			])
		]),
		...children
	]
}
</script>

<template>
	<div class="rounded-md border">
		<Toaster position="top-right" />
		<div class="p-4 border-b flex justify-between">
			<Input v-model="searchQuery" placeholder="Search groups..." class="max-w-md" />
			<CreateGroup/>
		</div>
		<div v-if="pendingChanges.length" class="bg-yellow-50 p-4 border-b flex items-center">
			<div class="flex-1">
				<p class="text-yellow-800 font-medium">
				<span class="animate-pulse">🔄</span>
				You have {{ pendingChanges.length }} pending {{ pendingChanges.length === 1 ? 'change' : 'changes' }} being processed.
				</p>
				<p class="text-yellow-700 text-sm">Changes will be applied shortly. The page will refresh automatically when complete.</p>
			</div>
		</div>

		<Table>
			<TableHeader>
			<TableRow>
			<TableHead class="w-[300px]">Name</TableHead>
			<TableHead>Description</TableHead>
			<TableHead class="w-[80px]">Media</TableHead>
			<TableHead class="w-[80px]">Members</TableHead>
			<TableHead class="w-[120px]">Moderation</TableHead>
			<TableHead class="w-[100px] text-right">Actions</TableHead>
			</TableRow>
			</TableHeader>
			<TableBody>
			<template v-if="filteredGroupTree.length">
				<template v-for="group in filteredGroupTree" :key="group.id">
					<component v-for="(rowVNode, index) in renderGroupRow(group)" :key="`${group.id}-${index}`" :is="rowVNode" />
				</template>
			</template>
			<TableRow v-else>
			<TableCell colspan="6" class="h-24 text-center text-muted-foreground">
			<h2 class="text-lg" v-if="loading">Loading Groups</h2>
			<h2 class="text-lg" v-else>

				No groups found.</h2>
			</TableCell>
			</TableRow>
			</TableBody>
		</Table>
	</div>
</template>

<style scoped>
.group-description {
	margin-top: 0.5rem;
	padding-left: 1.5rem;
	font-size: 0.9em;
	color: #666;
	line-height: 1.5;
}

.group-description :deep(p) {
	margin: 0.5em 0;
}

.group-description :deep(ul),
.group-description :deep(ol) {
	padding-left: 1.5em;
	margin: 0.5em 0;
}

.group-description :deep(a) {
	color: #3b82f6;
	text-decoration: underline;
}

/* Drag and drop styles */
.drag-over {
	background-color: #f0f9ff;
	border-top: 2px solid #3b82f6;
	border-bottom: 2px solid #3b82f6;
}

.dragging {
	opacity: 0.5;
}

.pending-change {
	background-color: #fffbeb;
}
/* Add to your <style> section */
.drag-over-folder {
	background-color: #f0f9ff !important;
	outline: 2px dashed #3b82f6 !important;
}

.drag-over-before::before,
.drag-over-after::after {
	content: "";
	position: absolute;
	left: 0;
	right: 0;
	height: 2px;
	background-color: #3b82f6;
	z-index: 10;
}

.drag-over-before::before {
	top: -1px;
}

.drag-over-after::after {
	bottom: -1px;
}

.drag-over-into {
	background-color: #f0f9ff !important;
	outline: 2px dashed #3b82f6 !important;
}

.drag-over-into::before {
	content: "Drop to move inside";
	position: absolute;
	right: 10px;
	font-size: 0.8em;
	color: #3b82f6;
}
.drag-over-into::after {
	content: "Drop to move inside";
	position: absolute;
	right: 10px;
	font-size: 0.8em;
	color: #3b82f6;
}
.drag-over-folder {
	background-color: #f0f9ff !important;
	outline: 2px dashed #3b82f6 !important;
}

.drag-over-folder::after {
	content: "Drop to add";
	position: absolute;
	right: 10px;
	font-size: 1.2em;
	color: #3b82f6;
}
/* Add these styles */
.drag-over-before {
	border-top: 2px solid #3b82f6;
	box-shadow: inset 0 2px 0 #3b82f6;
	 margin-top: 15px; 
}

.drag-over-after {
	border-bottom: 2px solid #3b82f6;
	box-shadow: inset 0 -2px 0 #3b82f6;
	 margin-bottom: 15px; 
}

.drag-over-into {
	background-color: #f0f9ff !important;
	outline: 2px dashed #3b82f6 !important;
}

.drag-over-into::after {
	content: "Drop to add";
	position: absolute;
	right: 10px;
	font-size: 0.8em;
	color: #3b82f6;
}
.ancestry-path {
	color: #64748b;
	font-size: 0.75rem;
	margin-right: 0.5rem;
}

</style>
